import { Fragment } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import {
  ChevronDownIcon,
  TruckBoxIcon,
  TruckIcon,
  TruckNopIcon,
} from '#/shared/ui/icons'
import { Tooltip } from '#/shared/ui/tooltip'

interface VehicleCellProps {
  name: string
  extraCount?: number
  hasEngine?: boolean
  isEnclosed?: boolean
  hasRequests?: boolean
  requestsCount?: number
  onRequestsClick?: () => void
  truckingCompany?: string
}

function RequestsBadge({
  count,
  onClick,
}: {
  count?: number
  onClick?: () => void
}) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick?.()
      }}
      className="flex h-6.5 shrink-0 cursor-pointer items-center gap-1.25 rounded-sm border border-gray-200 px-1.25 py-0.75 hover:bg-gray-50"
    >
      {count != null && count > 0 && (
        <span className="flex size-4.5 items-center justify-center rounded-full bg-orange-500 text-xs text-white">
          {count}
        </span>
      )}
      <span className="text-sm text-gray-900">{t('table.requests')}</span>
      <ChevronDownIcon className={cn('size-2.5 -rotate-90 text-gray-900')} />
    </button>
  )
}

function TruckingCompany({ name }: { name: string }) {
  return (
    <Tooltip content={name}>
      <span className="flex min-w-0 flex-1 items-center gap-1.25 text-sm text-gray-900">
        <TruckIcon className="size-3.5 shrink-0 text-orange-500" />
        <span className="min-w-0 flex-1 truncate">{name}</span>
      </span>
    </Tooltip>
  )
}

function ExtraCount({ count }: { count: number }) {
  const { t } = useTranslation()
  return (
    <span className="shrink-0 text-xs text-gray-500">
      {t('table.more', { count })}
    </span>
  )
}

export function VehicleCell({
  name,
  extraCount,
  hasEngine,
  isEnclosed,
  hasRequests,
  requestsCount,
  onRequestsClick,
  truckingCompany,
}: VehicleCellProps) {
  const showRequests =
    hasRequests === true || (requestsCount != null && requestsCount > 0)
  const showExtra = extraCount != null && extraCount > 0
  const showTrucking = !!truckingCompany

  const items: ReactNode[] = []
  if (showRequests)
    items.push(
      <RequestsBadge count={requestsCount} onClick={onRequestsClick} />,
    )
  if (showExtra) items.push(<ExtraCount count={extraCount} />)
  if (showTrucking) items.push(<TruckingCompany name={truckingCompany} />)

  return (
    <div className="flex w-46 flex-col items-start">
      <div className="flex h-7 w-full min-w-0 items-center gap-1.25">
        {hasEngine && (
          <TruckNopIcon className="size-4 shrink-0 text-destructive" />
        )}
        {isEnclosed && (
          <TruckBoxIcon className="size-4 shrink-0 text-gray-500" />
        )}
        <Tooltip content={name}>
          <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
            {name}
          </span>
        </Tooltip>
      </div>
      {items.length > 0 && (
        <div className="flex h-7 w-full min-w-0 items-center gap-1.25">
          {items.map((item, i) => (
            <Fragment key={i}>
              {i > 0 && <span className="text-xs text-gray-500">·</span>}
              {item}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  )
}
